/**
 * MASS EMAIL TEMPLATE GENERATOR
 * 
 * Generates mass broadcast emails with consistent Aureus Alliance branding.
 * Uses the same header/footer structure as existing email templates.
 * 
 * @version 1.0.0
 * @created 2025-10-23
 */

export interface MassEmailData {
 subject: string;
 htmlContent: string;
 customTitle?: string;
}

/**
 * Generate mass email HTML with Aureus Alliance branding
 */
export function generateMassEmailHTML(data: MassEmailData): string {
 const { subject, htmlContent, customTitle } = data;
 const title = customTitle || subject;

 return `
<!DOCTYPE html>
<html>
<head>
 <meta charset="utf-8">
 <meta name="viewport" content="width=device-width, initial-scale=1.0">
 <title>${title}</title>
</head>
<body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f4f4f4;">
 <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4; padding: 20px;">
 <tr>
 <td align="center">
 <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
 
 <!-- Header -->
 <tr>
 <td style="background: linear-gradient(135deg, #D4AF37 0%, #F4E5B0 100%); padding: 40px 20px; text-align: center;">
 <h1 style="margin: 0; color: #1a1a1a; font-size: 28px; font-weight: bold;">Aureus Alliance Holdings</h1>
 </td>
 </tr>

 <!-- Content -->
 <tr>
 <td style="padding: 40px 30px;">
 ${htmlContent}
 </td>
 </tr>

 <!-- Footer -->
 <tr>
 <td style="background-color: #F9FAFB; padding: 20px 30px; text-align: center; border-top: 1px solid #E5E7EB;">
 <p style="margin: 0; color: #6B7280; font-size: 12px;">
 Real Gold • Real Shares • Real Ownership
 </p>
 <p style="margin: 10px 0 0 0; color: #6B7280; font-size: 12px;">
 © ${new Date().getFullYear()} Aureus Alliance Holdings (Pty) Ltd. All rights reserved.
 </p>
 </td>
 </tr>

 </table>
 </td>
 </tr>
 </table>
</body>
</html>
 `.trim();
}

/**
 * Generate plain text version of mass email
 */
export function generateMassEmailText(data: MassEmailData): string {
 const { subject, htmlContent } = data;
 
 // Strip HTML tags and convert to plain text
 const plainText = htmlContent
 .replace(/<br\s*\/?>/gi, '\n')
 .replace(/<\/p>/gi, '\n\n')
 .replace(/<\/div>/gi, '\n')
 .replace(/<\/h[1-6]>/gi, '\n\n')
 .replace(/<[^>]+>/g, '')
 .replace(/&nbsp;/g, ' ')
 .replace(/&amp;/g, '&')
 .replace(/&lt;/g, '<')
 .replace(/&gt;/g, '>')
 .replace(/&quot;/g, '"')
 .replace(/&#39;/g, "'")
 .trim();

 return `
AUREUS ALLIANCE HOLDINGS

${subject.toUpperCase()}

${plainText}

---
Real Gold • Real Shares • Real Ownership
© ${new Date().getFullYear()} Aureus Alliance Holdings (Pty) Ltd. All rights reserved.
 `.trim();
}

/**
 * Validate email content
 */
export function validateEmailContent(subject: string, htmlContent: string): {
 valid: boolean;
 errors: string[];
} {
 const errors: string[] = [];

 if (!subject || subject.trim().length === 0) {
 errors.push('Subject line is required');
 }

 if (subject && subject.length > 200) {
 errors.push('Subject line must be 200 characters or less');
 }

 if (!htmlContent || htmlContent.trim().length === 0) {
 errors.push('Email content is required');
 }

 // Check if content is just empty HTML tags
 const textContent = htmlContent.replace(/<[^>]+>/g, '').trim();
 if (textContent.length === 0) {
 errors.push('Email content cannot be empty');
 }

 if (textContent.length > 50000) {
 errors.push('Email content is too long (max 50,000 characters)');
 }

 return {
 valid: errors.length === 0,
 errors
 };
}

